﻿package com.rosario.juridico.ui.screens

import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.navigation.NavController
import com.rosario.juridico.marketplace.model.Movimiento
import com.rosario.juridico.marketplace.model.Tarea
import com.rosario.juridico.marketplace.model.TiposMovimiento

import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.toLocalDateTime

private val AzulD  = Color(0xFF1565C0)
private val RojoD  = Color(0xFFC62828)
private val AmarD  = Color(0xFFF57C00)
private val VerdeD = Color(0xFF2E7D32)
private val GrisD  = Color(0xFF546E7A)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PantallaDetalleExpediente(
    expedienteId: String,
    navController: NavController,
    viewModel: DetalleExpedienteViewModel = viewModel { DetalleExpedienteViewModel() }
) {
    val tareas          by viewModel.tareas.collectAsState()
    val movimientos     by viewModel.movimientos.collectAsState()
    val loading         by viewModel.loading.collectAsState()
    val showDialogTarea by viewModel.showDialogNuevaTarea.collectAsState()
    val showDialogMov   by viewModel.showDialogMovimiento.collectAsState()
    val movEditar       by viewModel.movimientoEditar.collectAsState()
    val sugerenciaIA    by viewModel.sugerenciaIA.collectAsState()
    val loadingIA       by viewModel.loadingIA.collectAsState()
    


    LaunchedEffect(expedienteId) { viewModel.cargar(expedienteId) }

    val tareasPendientes = remember(tareas) { 
        tareas.filter { !it.completada }.sortedBy { it.fechaVencimiento } 
    }
    val tareasCompletadas = remember(tareas) { 
        tareas.filter { it.completada } 
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Detalle del Expediente", color = Color.White, fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = { navController.popBackStack() }) {
                        Icon(Icons.Default.ArrowBack, contentDescription = "Atrás", tint = Color.White)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = AzulD)
            )
        },
        floatingActionButton = {
            Column(horizontalAlignment = Alignment.End, verticalArrangement = Arrangement.spacedBy(12.dp)) {
                SmallFloatingActionButton(onClick = { viewModel.abrirDialogoMovimiento() }, containerColor = AzulD, contentColor = Color.White) {
                    Icon(Icons.Default.Timeline, contentDescription = "Nuevo Movimiento")
                }
                FloatingActionButton(onClick = { viewModel.toggleDialogoTarea(true) }, containerColor = AzulD, contentColor = Color.White) {
                    Icon(Icons.Default.Add, contentDescription = "Nueva Tarea")
                }
            }
        }
    ) { padding ->
        if (loading) {
            Box(Modifier.fillMaxSize().padding(padding), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AzulD)
            }
        } else {
            LazyColumn(
                modifier = Modifier.fillMaxSize().padding(padding),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                item(key = "resumen_header") {
                    ResumenCard(tareasPendientes = tareasPendientes)
                }

                item(key = "ia_asistente_section") {
                    BotoneraIA(
                        sugerencia = sugerenciaIA,
                        loading = loadingIA,
                        onSugerir = { viewModel.sugerirProximoPaso(null, movimientos) },
                        onLimpiar = { viewModel.limpiarSugerencia() }
                    )
                }

                if (movimientos.isNotEmpty()) {
                    item(key = "timeline_title") {
                        SeccionHeader("Timeline Procesal", Icons.Default.Timeline, AzulD)
                    }
                    items(movimientos, key = { it.id }) { mov ->
                        MovimientoTimelineItem(
                            movimiento = mov,
                            onEditar = { viewModel.abrirDialogoMovimiento(mov) },
                            onEliminar = { viewModel.eliminarMovimiento(expedienteId, mov.id) },
                            isReadOnly = false
                        )
                    }
                }

                item(key = "vencimientos_title") {
                    SeccionHeader("Próximos Vencimientos", Icons.Default.Schedule, AzulD)
                }
                
                val conVenc = tareasPendientes.filter { it.fechaVencimiento != null }
                if (conVenc.isEmpty()) {
                    item(key = "venc_vacio") { TextoVacioD("Sin vencimientos próximos") }
                } else {
                    items(conVenc, key = { it.id }) { tarea -> VencimientoItemD(tarea) }
                }

                item(key = "tareas_title") {
                    SeccionHeader("Pasos Procesales Pendientes", Icons.Default.ListAlt, AzulD)
                }
                if (tareasPendientes.isEmpty()) {
                    item(key = "tareas_vacia") { TextoVacioD("No hay tareas pendientes") }
                } else {
                    items(tareasPendientes, key = { it.id }) { tarea -> PasoItem(tarea) }
                }

                if (tareasCompletadas.isNotEmpty()) {
                    item(key = "completadas_title") {
                        SeccionHeader("Completadas (${tareasCompletadas.size})", Icons.Default.CheckCircle, VerdeD)
                    }
                    items(tareasCompletadas, key = { it.id }) { tarea -> TareaCompletadaItemD(tarea) }
                }

                item(key = "final_spacer") { Spacer(Modifier.height(80.dp)) }
            }
        }
    }

    if (showDialogTarea) {
        DialogNuevaTarea(
            onConfirmar = { desc, fechaMs -> viewModel.agregarTarea(expedienteId, desc, fechaMs) },
            onDismiss = { viewModel.toggleDialogoTarea(false) }
        )
    }

    if (showDialogMov) {
        DialogMovimiento(
            movimientoInicial = movEditar,
            onConfirmar = { mov -> viewModel.guardarMovimiento(expedienteId, mov) },
            onDismiss = { viewModel.cerrarDialogoMovimiento() }
        )
    }
}

@Composable
private fun ResumenCard(tareasPendientes: List<Tarea>) {
    val proximoVenc = remember(tareasPendientes) { tareasPendientes.firstOrNull { it.fechaVencimiento != null }?.fechaVencimiento }
    val urgente = remember(proximoVenc) {
        proximoVenc?.let { it - Clock.System.now().toEpochMilliseconds() <= 3 * 24 * 60 * 60 * 1000L } ?: false
    }
    val colorBorde = if (urgente) RojoD else AzulD

    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = colorBorde.copy(alpha = 0.05f)),
        border = CardDefaults.outlinedCardBorder()
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Icon(Icons.Default.Folder, null, tint = colorBorde, modifier = Modifier.size(20.dp))
                Text("Expediente", style = MaterialTheme.typography.labelMedium, color = colorBorde)
                if (urgente) {
                    Spacer(Modifier.weight(1f))
                    Surface(color = RojoD.copy(alpha = 0.1f), shape = RoundedCornerShape(6.dp)) {
                        Text("URGENTE", modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp), style = MaterialTheme.typography.labelSmall, color = RojoD, fontWeight = FontWeight.Bold)
                    }
                }
            }
            HorizontalDivider(color = colorBorde.copy(alpha = 0.15f))
            Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                Column {
                    Text("Pendientes", style = MaterialTheme.typography.labelSmall, color = GrisD)
                    Text(tareasPendientes.size.toString(), style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold, color = if (tareasPendientes.isNotEmpty()) AzulD else GrisD)
                }
                if (proximoVenc != null) {
                    Column {
                        Text("Próximo venc.", style = MaterialTheme.typography.labelSmall, color = GrisD)
                        Text(formatearFechaDetalle(proximoVenc), style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold, color = if (urgente) RojoD else AmarD)
                    }
                }
            }
        }
    }
}

@Composable
private fun BotoneraIA(sugerencia: String?, loading: Boolean, onSugerir: () -> Unit, onLimpiar: () -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Button(
            onClick = onSugerir,
            modifier = Modifier.fillMaxWidth(),
            enabled = !loading,
            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF6A1B9A)),
            shape = RoundedCornerShape(12.dp)
        ) {
            if (loading) {
                CircularProgressIndicator(color = Color.White, modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                Spacer(Modifier.width(8.dp))
                Text("Consultando IA...")
            } else {
                Icon(Icons.Default.AutoAwesome, null, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(8.dp))
                Text("IA sugiere próximo paso", fontWeight = FontWeight.Bold)
            }
        }

        // Estabilización de jerarquía en Web con Box y AnimatedVisibility
        Box(modifier = Modifier.fillMaxWidth()) {
            this@Column.AnimatedVisibility(visible = sugerencia != null, enter = expandVertically() + fadeIn(), exit = shrinkVertically() + fadeOut()) {
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    colors = CardDefaults.cardColors(containerColor = Color(0xFFF3E5F5))
                ) {
                    Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            Icon(Icons.Default.AutoAwesome, null, tint = Color(0xFF6A1B9A), modifier = Modifier.size(18.dp))
                            Text("Sugerencia IA", style = MaterialTheme.typography.labelMedium, color = Color(0xFF6A1B9A), fontWeight = FontWeight.Bold)
                            Spacer(Modifier.weight(1f))
                            IconButton(onClick = onLimpiar, modifier = Modifier.size(24.dp)) {
                                Icon(Icons.Default.Close, null, tint = GrisD, modifier = Modifier.size(16.dp))
                            }
                        }
                        Text(sugerencia ?: "", style = MaterialTheme.typography.bodySmall, color = Color(0xFF4A148C))
                    }
                }
            }
        }
    }
}

@Composable
private fun MovimientoTimelineItem(movimiento: Movimiento, onEditar: () -> Unit, onEliminar: () -> Unit, isReadOnly: Boolean = false) {
    val colorTipo = remember(movimiento.tipo) { colorParaTipo(movimiento.tipo) }
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Box(
                modifier = Modifier.size(36.dp).clip(CircleShape).background(colorTipo.copy(alpha = 0.15f)),
                contentAlignment = Alignment.Center
            ) {
                Icon(iconoParaTipo(movimiento.tipo), null, tint = colorTipo, modifier = Modifier.size(18.dp))
            }
            Box(modifier = Modifier.width(2.dp).height(24.dp).background(colorTipo.copy(alpha = 0.2f)))
        }
        Card(
            modifier = Modifier.weight(1f),
            shape = RoundedCornerShape(12.dp),
            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
            elevation = CardDefaults.cardElevation(1.dp)
        ) {
            Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Top) {
                    Column(modifier = Modifier.weight(1f)) {
                        Surface(color = colorTipo.copy(alpha = 0.1f), shape = RoundedCornerShape(4.dp)) {
                            Text(movimiento.tipo, modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp), style = MaterialTheme.typography.labelSmall, color = colorTipo, fontWeight = FontWeight.Bold)
                        }
                        Spacer(Modifier.height(4.dp))
                        Text(movimiento.descripcion, style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Medium)
                    }
                    if (!isReadOnly) {
                        var showMenu by remember { mutableStateOf(false) }
                        Box {
                            IconButton(onClick = { showMenu = true }, modifier = Modifier.size(24.dp)) {
                                Icon(Icons.Default.MoreVert, null, tint = GrisD, modifier = Modifier.size(16.dp))
                            }
                            DropdownMenu(expanded = showMenu, onDismissRequest = { showMenu = false }) {
                                DropdownMenuItem(text = { Text("Editar") }, onClick = { showMenu = false; onEditar() }, leadingIcon = { Icon(Icons.Default.Edit, null) })
                                DropdownMenuItem(text = { Text("Eliminar", color = RojoD) }, onClick = { showMenu = false; onEliminar() }, leadingIcon = { Icon(Icons.Default.Delete, null, tint = RojoD) })
                            }
                        }
                    }
                }
                Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    if (movimiento.fecha > 0L) {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                            Icon(Icons.Default.CalendarToday, null, tint = GrisD, modifier = Modifier.size(12.dp))
                            Text(formatearFechaDetalle(movimiento.fecha), style = MaterialTheme.typography.labelSmall, color = GrisD)
                        }
                    }
                }
            }
        }
    }
}

private fun colorParaTipo(tipo: String): Color = when (tipo) {
    "Demanda", "Contestación" -> Color(0xFF1565C0)
    "Audiencia", "Mediación"  -> Color(0xFF2E7D32)
    "Sentencia", "Decreto"    -> Color(0xFF6A1B9A)
    "Apelación"               -> Color(0xFFC62828)
    "Cédula", "Oficio"        -> Color(0xFFF57C00)
    else                      -> Color(0xFF546E7A)
}

private fun iconoParaTipo(tipo: String) = when (tipo) {
    "Demanda"      -> Icons.Default.Gavel
    "Audiencia"    -> Icons.Default.RecordVoiceOver
    "Sentencia"    -> Icons.Default.Balance
    "Apelación"    -> Icons.Default.ArrowUpward
    "Mediación"    -> Icons.Default.People
    "Cédula"       -> Icons.Default.Mail
    "Oficio"       -> Icons.Default.Description
    else           -> Icons.Default.Article
}

@Composable
private fun SeccionHeader(titulo: String, icono: androidx.compose.ui.graphics.vector.ImageVector, color: Color) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.padding(vertical = 4.dp)) {
        Icon(icono, null, tint = color, modifier = Modifier.size(18.dp))
        Text(titulo, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = color)
    }
}

@Composable
private fun VencimientoItemD(tarea: Tarea) {
    val ahora = remember { Clock.System.now().toEpochMilliseconds() }
    val msRestantes = (tarea.fechaVencimiento ?: 0L) - ahora
    val dias = (msRestantes / (1000 * 60 * 60 * 24)).toInt()
    val (color, etiqueta) = remember(dias) {
        when {
            dias < 0  -> RojoD   to "Vencido"
            dias <= 3 -> RojoD   to "En $dias días"
            dias <= 7 -> AmarD   to "En $dias días"
            else      -> VerdeD  to "En $dias días"
        }
    }
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = color.copy(alpha = 0.06f))
    ) {
        Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            Box(modifier = Modifier.size(40.dp).clip(CircleShape).background(color.copy(alpha = 0.12f)), contentAlignment = Alignment.Center) {
                Icon(Icons.Default.CalendarToday, null, tint = color, modifier = Modifier.size(20.dp))
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(tarea.descripcion, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.SemiBold)
                Text(formatearFechaDetalle(tarea.fechaVencimiento!!), style = MaterialTheme.typography.bodySmall, color = GrisD)
            }
            Surface(color = color.copy(alpha = 0.12f), shape = RoundedCornerShape(8.dp)) {
                Text(etiqueta, modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp), style = MaterialTheme.typography.labelSmall, color = color, fontWeight = FontWeight.Bold)
            }
        }
    }
}

@Composable
private fun PasoItem(tarea: Tarea) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        elevation = CardDefaults.cardElevation(1.dp)
    ) {
        Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            Box(modifier = Modifier.size(32.dp).clip(CircleShape).background(AzulD.copy(alpha = 0.1f)), contentAlignment = Alignment.Center) {
                Icon(Icons.Default.RadioButtonUnchecked, null, tint = AzulD, modifier = Modifier.size(16.dp))
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(tarea.descripcion, style = MaterialTheme.typography.bodyMedium)
                if (tarea.fechaVencimiento != null) {
                    Text("Vence: ${formatearFechaDetalle(tarea.fechaVencimiento!!)}", style = MaterialTheme.typography.labelSmall, color = AzulD)
                }
            }
            Icon(Icons.Default.ChevronRight, null, tint = GrisD.copy(alpha = 0.4f), modifier = Modifier.size(18.dp))
        }
    }
}

@Composable
private fun TareaCompletadaItemD(tarea: Tarea) {
    Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp, vertical = 4.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
        Icon(Icons.Default.CheckCircle, null, tint = VerdeD.copy(alpha = 0.7f), modifier = Modifier.size(18.dp))
        Text(tarea.descripcion, style = MaterialTheme.typography.bodySmall.copy(textDecoration = TextDecoration.LineThrough, color = GrisD))
    }
}

@Composable
private fun TextoVacioD(texto: String) {
    Text(texto, style = MaterialTheme.typography.bodySmall, color = GrisD, modifier = Modifier.padding(start = 8.dp, bottom = 4.dp))
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DialogNuevaTarea(onConfirmar: (String, Long?) -> Unit, onDismiss: () -> Unit) {
    var descripcion    by remember { mutableStateOf("") }
    var diaStr         by remember { mutableStateOf("") }
    var mesStr         by remember { mutableStateOf("") }
    var anioStr        by remember { mutableStateOf("") }
    var conVencimiento by remember { mutableStateOf(false) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Nueva Tarea") },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                OutlinedTextField(value = descripcion, onValueChange = { descripcion = it }, label = { Text("Descripción") }, modifier = Modifier.fillMaxWidth())
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Checkbox(checked = conVencimiento, onCheckedChange = { conVencimiento = it })
                    Text("Tiene vencimiento")
                }
                if (conVencimiento) {
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        OutlinedTextField(value = diaStr, onValueChange = { if (it.length <= 2) diaStr = it.filter { c -> c.isDigit() } }, label = { Text("Día") }, modifier = Modifier.weight(1f))
                        OutlinedTextField(value = mesStr, onValueChange = { if (it.length <= 2) mesStr = it.filter { c -> c.isDigit() } }, label = { Text("Mes") }, modifier = Modifier.weight(1f))
                        OutlinedTextField(value = anioStr, onValueChange = { if (it.length <= 4) anioStr = it.filter { c -> c.isDigit() } }, label = { Text("Año") }, modifier = Modifier.weight(1.5f))
                    }
                }
            }
        },
        confirmButton = {
            Button(onClick = {
                val fechaMs = if (conVencimiento) parsearFechaAMs(diaStr, mesStr, anioStr) else null
                onConfirmar(descripcion, fechaMs)
            }, enabled = descripcion.isNotBlank()) { Text("Agregar") }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancelar") } }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DialogMovimiento(movimientoInicial: Movimiento?, onConfirmar: (Movimiento) -> Unit, onDismiss: () -> Unit) {
    var tipo by remember { mutableStateOf(movimientoInicial?.tipo ?: "") }
    var descripcion by remember { mutableStateOf(movimientoInicial?.descripcion ?: "") }
    var diaStr by remember { mutableStateOf("") }
    var mesStr by remember { mutableStateOf("") }
    var anioStr by remember { mutableStateOf("") }
    var tipoExpanded by remember { mutableStateOf(false) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(if (movimientoInicial == null) "Nuevo Movimiento" else "Editar") },
        text = {
            Column(modifier = Modifier.verticalScroll(rememberScrollState()), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                ExposedDropdownMenuBox(expanded = tipoExpanded, onExpandedChange = { tipoExpanded = it }) {
                    OutlinedTextField(value = tipo, onValueChange = {}, readOnly = true, label = { Text("Tipo") }, trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = tipoExpanded) }, modifier = Modifier.fillMaxWidth().menuAnchor())
                    ExposedDropdownMenu(expanded = tipoExpanded, onDismissRequest = { tipoExpanded = false }) {
                        TiposMovimiento.todos.forEach { t ->
                            DropdownMenuItem(text = { Text(t) }, onClick = { tipo = t; tipoExpanded = false })
                        }
                    }
                }
                OutlinedTextField(value = descripcion, onValueChange = { descripcion = it }, label = { Text("Descripción") }, modifier = Modifier.fillMaxWidth())
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedTextField(value = diaStr, onValueChange = { if (it.length <= 2) diaStr = it.filter { c -> c.isDigit() } }, label = { Text("Día") }, modifier = Modifier.weight(1f))
                    OutlinedTextField(value = mesStr, onValueChange = { if (it.length <= 2) mesStr = it.filter { c -> c.isDigit() } }, label = { Text("Mes") }, modifier = Modifier.weight(1f))
                    OutlinedTextField(value = anioStr, onValueChange = { if (it.length <= 4) anioStr = it.filter { c -> c.isDigit() } }, label = { Text("Año") }, modifier = Modifier.weight(1.5f))
                }
            }
        },
        confirmButton = {
            Button(onClick = {
                val f = parsearFechaAMs(diaStr, mesStr, anioStr) ?: Clock.System.now().toEpochMilliseconds()
                onConfirmar((movimientoInicial ?: Movimiento()).copy(tipo = tipo, descripcion = descripcion.trim(), fecha = f))
            }, enabled = tipo.isNotBlank() && descripcion.isNotBlank()) { Text("Guardar") }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancelar") } }
    )
}

private fun parsearFechaAMs(dia: String, mes: String, anio: String): Long? {
    return try {
        val d = dia.toInt(); val m = mes.toInt(); val a = anio.toInt()
        if (d < 1 || d > 31 || m < 1 || m > 12 || a < 2000) return null
        LocalDate(a, m, d).atStartOfDayIn(TimeZone.currentSystemDefault()).toEpochMilliseconds()
    } catch (e: Exception) { null }
}

fun formatearFechaDetalle(ms: Long): String {
    val instant = Instant.fromEpochMilliseconds(ms)
    val localDate = instant.toLocalDateTime(TimeZone.currentSystemDefault()).date
    return "${localDate.dayOfMonth}/${localDate.monthNumber}/${localDate.year}"
}
